import React, { useRef, useState } from 'react';
import { ISelectOption, TSelectValue } from 'components/Select/types';
import { SelectProps, DefaultOptionType } from 'antd/lib/select';
import { useAdditionalValidation } from 'components/WithAdditionalValidation/useAdditionalValidation';
import { Select } from 'antd';
import { useTranslation } from 'react-i18next';
import breadcrumbs from 'assets/img/breadcrumbs.svg';
import { Icon, IconName } from 'components/Icon/Icon';
import { SelectAllHeader } from 'components/Select/SelectAllHeader';
import SelectDropdownReloader from 'components/SelectDropdownReloader/SelectDropdownReloader';
import Text from 'components/Text/Text';
import classNames from 'classnames';
import style from './OutsideFormSelectNew.module.scss';

const { Option } = Select;

type TDefaultSelectProps = Omit<SelectProps, 'options'>;

interface IProps extends TDefaultSelectProps {
	name: string;
	label: string;
	value: TSelectValue;
	errorMessage?: string;
	blockId?: string;
	options?: ISelectOption[];
	loading?: boolean;
	fetchError?: boolean;
	placeholder?: string;
	className?: string;
	labelClassName?: string;
	containerClassName?: string;
	withSearch?: boolean;
	withSelectAll?: boolean;
	keepSearchWithDropdownRender?: boolean;
	counterValue?: number;
	testId?: string;
	withValidation?: boolean;
	withEmptyOption?: boolean;
	onRefetch?: () => void;
	onChange?: (value: any) => void;
}

export const OutsideFormSelectNew: React.FC<IProps> = React.memo(
	({
		name,
		blockId,
		label,
		value,
		options = [],
		loading = false,
		fetchError,
		placeholder = 'selectPlaceholder',
		className,
		labelClassName,
		containerClassName,
		withSearch = true,
		withSelectAll = true,
		keepSearchWithDropdownRender = false,
		counterValue,
		errorMessage = 'commonError',
		testId,
		withValidation = true,
		withEmptyOption = true,
		disabled,
		mode,
		dropdownRender,
		onRefetch,
		onChange,
		onDropdownVisibleChange,
		...restProps
	}) => {
		const parentNodeRef = useRef<HTMLSpanElement>(null);
		const [isDropdownOpen, setIsDropdownOpen] = useState(false);
		const { isShowValidationError, validationErrorMessage } = useAdditionalValidation(
			value,
			errorMessage,
			name,
			blockId,
		);
		const { t } = useTranslation('form');

		const isError = withValidation && isShowValidationError;

		const selectContainerClasses = classNames(style.container, containerClassName);
		const selectClasses = classNames(
			style.select,
			{ [style.selectError]: isError },
			{ [style.withoutInnerItem]: !!counterValue },
			className,
		);
		const nameClasses = classNames(style.name, { [style.disabledName]: disabled });
		const selectedCounterClasses = classNames(style.selectedCounter, {
			[style.disabledSelectedCounter]: disabled,
		});
		const optionClasses = (disabled: boolean) => classNames(style.listItem, { [style.disabledItem]: disabled });

		const isMultiple = mode === 'multiple';
		const isSearchEnabled = keepSearchWithDropdownRender ? withSearch : !dropdownRender && withSearch;
		const arrowIcon = loading ? undefined : (
			<>
				<Icon name={IconName.chevronDown} size="lg" className={style.arrowIcon} />
				<Icon name={IconName.search} size="lg" className={style.searchIcon} />
			</>
		);
		const removeIcon = (
			<svg className={style.iconClose}>
				<use xlinkHref={`${breadcrumbs}#closeSelectItem`} data-test="closeSelectItem" />
			</svg>
		);

		const handleRefetch = () => {
			onRefetch?.();
		};

		// Set null instead of undefined after clearing the select
		const handleChange = (newValue: TSelectValue | undefined) => {
			onChange?.(newValue === undefined ? null : newValue);
		};

		const handleDropdownVisibleChange = (open: boolean) => {
			setIsDropdownOpen(open);
			onDropdownVisibleChange?.(open);
		};

		const handleSearch = (inputValue: string, option: DefaultOptionType | undefined) => {
			if (option?.children && Array.isArray(option.children)) {
				return String(option.children[1]?.props?.translationKey)
					.toLowerCase()
					.includes(inputValue.toLowerCase());
			}
			return false;
		};

		const noDataContent = (
			<div className={style.noData}>
				<Icon name={IconName.moodBad} size="xxl" className={style.noDataIcon} />
				<Text className={style.noDataText} translationNameSpace="form" translationKey="noData" size="xs" />
			</div>
		);
		const notFoundContent = fetchError ? <SelectDropdownReloader onReload={handleRefetch} /> : noDataContent;

		const renderSelectedLabel = (option: ISelectOption) => (
			<span className={style.selectedLabel}>
				{option.icon}
				<Text
					className={style.selectedLabelText}
					translationKey={option.name}
					withTooltip={true}
					isNewTooltip={true}
					size="m"
				/>
				{option.additionalContent}
			</span>
		);

		const renderMultipleDropdown = (menu: React.ReactElement) => (
			<>
				<SelectAllHeader
					options={options}
					value={Array.isArray(value) ? value : []}
					withSelectAll={withSelectAll}
					onChange={handleChange}
				/>
				{dropdownRender ? dropdownRender(menu) : menu}
			</>
		);

		return (
			<div className={selectContainerClasses} data-test={testId}>
				<div className={style.labelRow}>
					<Text
						className={labelClassName}
						translationNameSpace="form"
						translationKey={label}
						size="xs"
						theme="neutralDark"
						display="inline"
					/>
				</div>
				{!!counterValue && !isDropdownOpen && (
					<Text
						className={selectedCounterClasses}
						translationNameSpace="form"
						translationKey="selectedItemsCounter"
						translationOptions={{ counterValue }}
						size="xs"
					/>
				)}
				<span ref={parentNodeRef} className={style.popupContainer} />
				<Select
					className={selectClasses}
					value={value}
					onChange={handleChange}
					loading={loading}
					showArrow={true}
					suffixIcon={arrowIcon}
					allowClear={!isMultiple}
					disabled={disabled}
					showSearch={isSearchEnabled}
					filterOption={isSearchEnabled ? handleSearch : undefined}
					dropdownRender={isMultiple ? renderMultipleDropdown : dropdownRender}
					onDropdownVisibleChange={handleDropdownVisibleChange}
					notFoundContent={notFoundContent}
					listItemHeight={options.length}
					getPopupContainer={(triggerNode) => parentNodeRef.current ?? triggerNode}
					placeholder={!counterValue ? t(placeholder) : undefined}
					removeIcon={!counterValue ? removeIcon : undefined}
					optionLabelProp={isMultiple ? 'children' : 'label'}
					mode={mode}
					{...restProps}>
					{options.length ? (
						<>
							{!isMultiple && withEmptyOption && (
								<Option
									className={classNames(style.listItem, style.hintItem)}
									value="empty"
									disabled={true}
									data-test="value-empty">
									<Text
										translationNameSpace="form"
										translationKey="selectAnyItemPlaceholder"
										asFragment={true}
									/>
								</Option>
							)}
							{options.map((el) => {
								return (
									<Option
										className={optionClasses(!!el.disabled)}
										value={el.id}
										key={el.id}
										label={renderSelectedLabel(el)}
										disabled={el.disabled}
										data-test={`value-${el.name}`}>
										{isMultiple ? (
											<span className={style.optionPrefix}>
												<Icon
													name={IconName.checkboxUnchecked}
													size="lg"
													className={style.checkboxUnchecked}
												/>
												<Icon
													name={IconName.checkboxChecked}
													size="lg"
													className={style.checkboxChecked}
												/>
												{el.icon}
											</span>
										) : (
											el.icon
										)}
										<Text
											className={nameClasses}
											translationKey={el.name}
											withTooltip={true}
											isNewTooltip={true}
											size="xs"
											weight="medium"
										/>
										{el.additionalContent}
									</Option>
								);
							})}
						</>
					) : null}
				</Select>
				{isError && (
					<div className={style.errorMessage}>
						<Text
							translationNameSpace="error"
							translationKey={validationErrorMessage}
							theme="error"
							size="xs"
							display="inline"
						/>
					</div>
				)}
			</div>
		);
	},
);
